import { useRef, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { type TFunction } from 'i18next';
import { isFuture } from 'date-fns';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { HandleType } from '@/enums/handle-type.enum';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { Box } from '@/UI/Box/Box';
import { Input as AppInput } from '@/UI/Input/Input';
import { Dropdown, Option } from '@/UI/Dropdown/Dropdown';
import { IndicatorTag } from '@/UI/Tag/IndicatorTag';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { XIcon, RevertAsInputIcon, SetAsInputIcon } from '@/UI/Icons';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { TextArea } from '@/UI/TextArea/TextArea';
import { NodeTextField } from '@/UI/NodeTextField/NodeTextField';
import { clampValue } from '@/utils/numbers';
import { TEXTAREA_BASE_HEIGHT, getMediaFramePlaceholderSx } from '@/consts/node-ui.consts';
import { RESOLUTION_OPTION_LABELS } from '@/components/Nodes/ModelComponents/consts/model-image-size.const';
import { ColorPickerField } from '@/UI/ColorPicker/ColorPickerField';
import { useParameterView } from '../Recipe/FlowContext';
import { NumberInputField as NumberInputFieldV2 } from './input-fields/number-input-field/number-input-fieldV2.component';
import { NumberInput } from './Shared/NumberInput';
import { ImageSizeInput } from './input-fields/image-size-field/ImageSizeInput';
import { vecToWebColor, webColorToVec } from './ModelComponents/colorParam.utils';
import { GizmoVectorField } from './ModelComponents/GizmoVectorField';
import type { NodeId, ParameterValue } from 'web';
import type { ParameterInfo } from '@/types/node';

// option -> show "New" tag until this date; entries past their date are inert
const NEW_ENUM_OPTIONS = new Map<string, string>([['Wonder 3', '2026-07-05']]);

const isNewOption = (option: string): boolean => {
  const newUntil = NEW_ENUM_OPTIONS.get(option);
  return !!newUntil && isFuture(new Date(newUntil));
};

const EnumOptionLabel = ({ text }: { text: string }) => {
  const { t } = useTranslation();
  return (
    <FlexCenVer sx={{ gap: 1 }}>
      <EllipsisText variant="body-sm-rg" color="inherit">
        {text}
      </EllipsisText>
      <IndicatorTag text={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NEW_INDICATOR)} />
    </FlexCenVer>
  );
};

/** Dropdown label for an enum option: renames known tokens and adds the "New" tag. */
const getEnumOptionLabel = (key: string, option: string | number): ReactNode => {
  const raw = String(option);
  const text = (key === 'resolution' ? RESOLUTION_OPTION_LABELS[raw] : undefined) ?? raw;
  return isNewOption(raw) ? <EnumOptionLabel text={text} /> : text;
};

export const ParamExposureComponent = ({
  nodeId,
  paramKey,
  isExposed,
  disabled,
}: {
  nodeId: NodeId;
  paramKey: string;
  isExposed: boolean;
  disabled?: boolean;
}) => {
  const view = useParameterView(nodeId);
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const { t } = useTranslation();

  return (
    <AppIconButton
      aria-label={t(isExposed ? I18N_KEYS.GENERAL.ARIA.COLLAPSE_PARAMETER : I18N_KEYS.GENERAL.ARIA.EXPOSE_PARAMETER)}
      disabled={disabled}
      mode="on-dark"
      width={24}
      height={24}
      onClick={isExposed ? () => view.collapseParameter(paramKey) : () => view.exposeParameter(paramKey, nodeTypes)}
    >
      <FlexCenVer sx={{ '&:hover': { color: token.text } }}>
        {isExposed ? <RevertAsInputIcon /> : <SetAsInputIcon />}
      </FlexCenVer>
    </AppIconButton>
  );
};

export const TextFieldWithCursor = ({ id, value, property, handleChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);

  useEffect(() => {
    // If we have a stored cursor position and the input is focused
    if (cursorPosition !== null && inputRef.current === document.activeElement) {
      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [value, cursorPosition]);

  const handleInputChange = (e) => {
    const newCursorPosition = e.target.selectionStart;
    setCursorPosition(newCursorPosition);
    handleChange(id, e.target.value);
  };

  return (
    <TextArea
      disabled={property.exposed}
      id={id}
      value={value}
      minRows={8}
      maxRows={16}
      onChange={handleInputChange}
      inputRef={inputRef}
    />
  );
};

export const renderEmptyState = (
  nodeOutputType: HandleType | undefined,
  isProcessing: boolean,
  t: TFunction,
  isDiagonalResizeEnabled: boolean,
) => {
  switch (nodeOutputType) {
    case HandleType.Text:
      return (
        <FlexCol
          sx={{
            bgcolor: color.Black84,
            width: '100%',
            position: 'relative',
            flex: 1,
            minHeight: 0,
          }}
        >
          <NodeTextField
            multiline
            minHeight={TEXTAREA_BASE_HEIGHT}
            fullWidth
            size="small"
            placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.TEXT_MODEL.EMPTY_STATE_PLACEHOLDER)}
            readOnly
          />
          <Flex
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {isProcessing && (
              <CircularProgress
                size={24}
                sx={{
                  color: token.textSecondary,
                }}
              />
            )}
          </Flex>
        </FlexCol>
      );
    default: {
      return (
        <Flex
          className="media-container-dark"
          sx={{
            width: '100%',
            ...getMediaFramePlaceholderSx(nodeOutputType, isDiagonalResizeEnabled),
            position: 'relative',
          }}
        >
          {/* `alignSelf` rather than `height: '100%'`: under diagonal resize the checkerboard is sized
              by `flex: 1` + `min-height`, which is not a definite height for percentage resolution, so
              `height: 100%` collapsed this box to the spinner and pinned the loader to the top. */}
          <Flex
            sx={{
              width: '100%',
              alignSelf: 'stretch',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {isProcessing && (
              <CircularProgress
                size={24}
                sx={{
                  color: token.textSecondary,
                }}
              />
            )}
          </Flex>
        </Flex>
      );
    }
  }
};

type SeedParameterValue = Extract<ParameterValue, { type: 'seed' }>['value'];

function SeedField({
  handleChange,
  isExposed,
  parameterKey,
  value,
}: {
  handleChange: (key: string, value: ParameterValue, ongoing?: boolean) => void;
  isExposed: boolean;
  parameterKey: string;
  value: SeedParameterValue;
}) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const randomToggle = (
    <AppCheckbox
      key="random-toggle"
      label={i18n.t(I18N_KEYS.RECIPE_MAIN.NODES.SEED.RANDOM)}
      checked={value.isRandom}
      onChange={(event) =>
        handleChange(parameterKey, { type: 'seed', value: { ...value, isRandom: event.target.checked } })
      }
      disabled={isExposed}
    />
  );
  const seedInput = (
    <NumberInput
      key="seed-input"
      value={value.seed}
      inputProps={{ type: 'number' }}
      decimals={0}
      size="small"
      onSubmit={(nextValue) =>
        handleChange(parameterKey, {
          type: 'seed',
          value: { ...value, seed: Math.round(clampValue(nextValue, 0, Number.MAX_SAFE_INTEGER)) },
        })
      }
      disabled={value.isRandom || isExposed}
      sx={{ ml: 1 }}
    />
  );

  return (
    <FlexCenVer sx={{ gap: isFplMigrationEnabled ? 1 : 2, width: '100%' }}>
      {isFplMigrationEnabled ? [seedInput, randomToggle] : [randomToggle, seedInput]}
    </FlexCenVer>
  );
}

export const renderComponent = (
  key: string,
  value: ParameterValue,
  info: ParameterInfo,
  isExposed: boolean,
  handleChange: (key: string, value: ParameterValue, ongoing?: boolean) => void,
  showMatchInput: boolean = true,
  lockAspectRatioByDefault: boolean = true,
  useAspectRatioLabels: boolean = false,
  step?: number,
) => {
  const type = info.type;
  switch (type) {
    case 'integer_with_limits':
      return value.type === 'integer' ? (
        <NumberInputFieldV2
          key={`${key}-numberInputField`}
          inputKey={key}
          title={info.title}
          value={value.value}
          disabled={isExposed}
          min={info.min}
          max={info.max}
          type="integer"
          onChange={(_, newValue, ongoing) =>
            handleChange(key, { type: 'integer', value: Math.round(newValue) }, ongoing)
          }
        />
      ) : null;
    case 'float_with_limits':
      return value.type === 'float' ? (
        <NumberInputFieldV2
          key={`${key}-numberInputField`}
          inputKey={key}
          title={info.title}
          value={value.value}
          disabled={isExposed}
          min={info.min}
          max={info.max}
          type="float"
          step={step}
          onChange={(_, newValue, ongoing) => handleChange(key, { type: 'float', value: newValue }, ongoing)}
        />
      ) : null;
    case 'integer':
      return value.type === 'integer' ? (
        <NumberInput
          key={`${key}-input-integer`}
          fullWidth
          disabled={isExposed}
          value={value.value}
          size="small"
          inputProps={{ type: 'number' }}
          onSubmit={(e) => handleChange(key, { type: 'integer', value: Math.round(e) })}
          decimals={0}
        />
      ) : null;
    case 'integer_selector':
      return value.type === 'integer' ? (
        <Dropdown
          width="100%"
          matchTriggerWidth
          value={value.value}
          disabled={isExposed}
          onChange={(o: Option<number>) => handleChange(key, { type: 'integer', value: o.value })}
          options={
            info.options?.map((o) => ({
              id: `integer-${o}`,
              label: o,
              value: o,
            })) ?? []
          }
          size="large"
        />
      ) : null;
    case 'float':
      return value.type === 'float' ? (
        <NumberInput
          key={`${key}-input-float`}
          fullWidth
          disabled={isExposed}
          value={value.value}
          size="small"
          inputProps={{ type: 'number', step: '0.1' }}
          onSubmit={(e) => handleChange(key, { type: 'float', value: e })}
          decimals={2}
        />
      ) : null;
    case 'enum':
      return value.type === 'string' || value.type === 'integer' ? (
        <Dropdown
          width="100%"
          matchTriggerWidth
          value={value.value}
          disabled={isExposed}
          onChange={(o: Option<string>) =>
            handleChange(
              key,
              value.type === 'integer'
                ? { type: 'integer', value: parseInt(o.value) }
                : { type: 'string', value: o.value },
            )
          }
          options={
            info.options?.map((o) => ({
              id: o,
              label: getEnumOptionLabel(key, o),
              value: o,
            })) ?? []
          }
          size="large"
        />
      ) : null;

    case 'boolean':
      return value.type === 'boolean' ? (
        <AppCheckbox
          checked={value.value}
          onChange={(e) => handleChange(key, { type: 'boolean', value: e.target.checked })}
          disabled={isExposed}
          label={info.title}
        />
      ) : null;
    case 'seed':
      return value.type === 'seed' ? (
        <SeedField parameterKey={key} value={value.value} isExposed={isExposed} handleChange={handleChange} />
      ) : null;
    case 'string_array':
      return value.type === 'string_array' ? (
        <ArrayInput
          value={value.value}
          itemKey={key}
          exposed={isExposed}
          type="string"
          handleChange={(key, value) => handleChange(key, { type: 'string_array', value })}
        />
      ) : null;
    case 'string':
      return value.type === 'string' ? (
        <TextFieldWithCursor
          id={key}
          value={value.value}
          property={{ exposed: isExposed }}
          handleChange={(key, value) => handleChange(key, { type: 'string', value: value.toString() })}
        />
      ) : null;
    case 'image_size':
      return value.type === 'image_size' ? (
        <ImageSizeInput
          key={`${key}-image-size`}
          value={value.value}
          onChange={(newValue) => handleChange(key, { type: 'image_size', value: newValue })}
          options={info.options}
          disabled={isExposed}
          showMatchInput={showMatchInput}
          lockAspectRatioByDefault={lockAspectRatioByDefault}
          useAspectRatioLabels={useAspectRatioLabels}
        />
      ) : null;
    case 'file_array':
      // file array is not supported yet as model input
      return null;
    case 'point':
      return value.type === 'vec2' ? (
        <GizmoVectorField
          key={`${key}-point`}
          type="point"
          value={value}
          label={info.title}
          disabled={isExposed}
          onChange={(next) => handleChange(key, next, false)}
        />
      ) : null;
    case 'point_radius':
      return value.type === 'vec3' ? (
        <GizmoVectorField
          key={`${key}-point-radius`}
          type="point_radius"
          value={value}
          label={info.title}
          disabled={isExposed}
          onChange={(next) => handleChange(key, next, false)}
        />
      ) : null;
    case 'angle_radius':
      return value.type === 'vec4' ? (
        <GizmoVectorField
          key={`${key}-angle-radius`}
          type="angle_radius"
          value={value}
          label={info.title}
          disabled={isExposed}
          onChange={(next) => handleChange(key, next, false)}
        />
      ) : null;
    case 'color':
      // Reuse the shared right-panel color field (hex + alpha row, same as the compositor/painter
      // color inputs). Its trigger has no delete/reorder affordances — those live in the palette-list
      // wrapper, not the field. `colorParam.utils` bridges vec3/vec4 [0,1] <-> WebColor (0-255 rgb).
      return value.type === 'vec3' || value.type === 'vec4' ? (
        <ColorPickerField
          key={`${key}-color`}
          color={vecToWebColor(value.value)}
          onChange={(c) => handleChange(key, webColorToVec(c, value.type === 'vec4'), true)}
          onChangeEnd={(c) => handleChange(key, webColorToVec(c, value.type === 'vec4'), false)}
          showAlpha={value.type === 'vec4'}
          showColorEditor
          fullWidth
          disabled={isExposed}
        />
      ) : null;
    case 'line':
      return value.type === 'vec4' ? (
        <GizmoVectorField
          key={`${key}-line`}
          type="line"
          value={value}
          label={info.title}
          disabled={isExposed}
          onChange={(next) => handleChange(key, next, false)}
        />
      ) : null;
    default: {
      const _exhaustiveCheck: never = type;
    }
  }
};

const ArrayInput = ({
  value,
  itemKey: key,
  exposed,
  type,
  handleChange,
}: {
  value: string[] | number[];
  itemKey: string;
  exposed: boolean;
  type: string;
  handleChange: (key: string, value: any) => void;
}) => {
  const { t } = useTranslation();

  const addItem = useCallback(() => {
    handleChange(key, [...value, type === 'string' ? '' : 0]);
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [value, handleChange, key]);

  return (
    <FlexCol sx={{ gap: 1, width: '100%' }}>
      {value.map((item, index) => (
        <FlexCenVer sx={{ gap: 1 }} key={`${key}-${index}`}>
          <TextSmRg sx={{ opacity: exposed ? 0.5 : 1, width: '12px' }}>{index + 1}</TextSmRg>
          <AppInput
            disabled={exposed}
            fullWidth
            id={`${key}-${index}`}
            value={item}
            placeholder={t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.MODEL_PROPERTIES.LIST_ITEM_PLACEHOLDER)}
            onChange={(e) => {
              const inputValue: string = e.target.value;
              handleChange(key, [...value.slice(0, index), inputValue.toString(), ...value.slice(index + 1)]);
            }}
            size="small"
          />
          <AppIconButton
            aria-label={t(I18N_KEYS.GENERAL.ARIA.REMOVE_ITEM)}
            disabled={exposed}
            mode="on-dark"
            width={24}
            height={24}
            onClick={() => handleChange(key, [...value.slice(0, index), ...value.slice(index + 1)])}
          >
            <XIcon width={16} height={16} />
          </AppIconButton>
        </FlexCenVer>
      ))}

      <Box sx={{ width: 'fit-content' }}>
        <ButtonContained onClick={addItem} size="small" mode="outlined" disabled={exposed}>
          {t(I18N_KEYS.RECIPE_MAIN.FLOW.DRAWER.MODEL_PROPERTIES.ADD_LIST_ITEM)}
        </ButtonContained>
      </Box>
    </FlexCol>
  );
};
